<script setup lang="ts">
/**
 * White-label tab (T07) — static informational content.
 *
 * The sprint GOAL.md non-goals call out that white-label provisioning happens
 * outside the dashboard; this tab is a landing/info page explaining what's
 * possible and how to start. Concrete provisioning UI is intentionally
 * deferred to a future sprint.
 */
import CopyCodeBlock from './CopyCodeBlock.vue';

const SNIPPET = `# Example: white-label hub
HUB_BRAND_NAME=acme-ai
HUB_BRAND_LOGO_URL=https://cdn.acme.ai/logo.svg
HUB_BRAND_PRIMARY_COLOR=#3b82f6
HUB_BRAND_DOMAIN=ai.acme.com
ROUTER_BASE_URL=https://api.opengonka.com/v1`;
</script>

<template>
  <div class="flex flex-col gap-6">
    <section class="card p-6">
      <h2 class="text-lg font-semibold">White-label Deployments</h2>
      <p class="text-sm text-muted mt-2">
        Run the Gonka proxy and hub under your own brand. The router is fully OpenAI-compatible
        and stateless from the customer's point of view, so you can layer your own billing,
        pricing markup, and identity on top while we handle decentralized inference routing.
      </p>

      <h3 class="text-base font-semibold mt-6">What's included</h3>
      <ul class="mt-2 list-disc list-inside text-sm text-muted space-y-1">
        <li>Custom domain (e.g. <code>ai.yourcompany.com</code>) with your TLS certificate.</li>
        <li>Brand assets (logo, primary color, favicon) injected at build time.</li>
        <li>Your own Stripe / on-chain payout account for top-ups.</li>
        <li>Independent rate limits, markup percentages, and welcome-bonus settings.</li>
        <li>Optional <em>Powered by Gonka</em> footer or full unbranded mode.</li>
      </ul>

      <h3 class="text-base font-semibold mt-6">Configuration snippet</h3>
      <p class="text-sm text-muted mt-2">
        Drop these into your container's environment to start a branded deployment. Full schema
        is documented in <code>services/hub/.env.example</code> in the source tree.
      </p>
      <div class="mt-3">
        <CopyCodeBlock :code="SNIPPET" language="bash" />
      </div>

      <h3 class="text-base font-semibold mt-6">Get started</h3>
      <p class="text-sm text-muted mt-2">
        White-label provisioning is handled out-of-band today — contact us to scope your
        deployment, agree commercial terms, and receive a kickoff playbook. A self-serve UI for
        white-label settings is on the roadmap.
      </p>
    </section>
  </div>
</template>
